import { useQuery } from '@tanstack/react-query'

import { Notice } from '../chrome/list-screen'
import { Button } from '../ui/button'
import { Drawer } from '../ui/dialog'
import { api, type RecipientPreview } from '../../lib/api'

/**
 * §64's review preview — the letter, and the evidence behind the address.
 *
 * **What a reviewer was being asked to approve, and could not see.** The queue showed an email
 * address, a status chip and two buttons; §64 asks for *"contact evidence, a personalization
 * preview, and Approve / Edit / Reject / Suppress"*. Approving a person rather than a letter is
 * the wrong act, and it was the only act this screen offered until 2026-08-22.
 *
 * The API renders it through the dispatcher's own renderer and runs §30's preflight over the
 * result, so what is on screen is what would be sent — not a second, simpler rendering that
 * eventually disagrees with the first.
 *
 * ### Three things it says out loud
 *
 * `notes` is rendered verbatim rather than summarised. It carries the placeholder unsubscribe link,
 * the absent personalization layer (§26 is Phase 6), and a mailbox that could not send — each a
 * real difference between this preview and the message. A preview that quietly omits them is a
 * promise it cannot keep.
 *
 * ### Edit is not here, and that is deliberate
 *
 * §64's four verbs are Approve / Edit / Reject / Suppress. **Edit** means editing the
 * *personalization* for one recipient, which has no model and no endpoint — Phase 6. The control is
 * absent rather than present-and-dead. **Suppress** is a real endpoint on its own screen and is not
 * duplicated here: suppression is permanent and organization-wide (§32), which is a different
 * decision from "not this campaign" and must not sit one pixel from Reject.
 */
export function RecipientPreviewDrawer({
  campaignId,
  campaignContactId,
  onClose,
  mayReview,
  onApprove,
  onReject,
}: {
  campaignId: string
  campaignContactId: string | null
  onClose: () => void
  mayReview: boolean
  onApprove: (campaignContactId: string) => void
  onReject: (campaignContactId: string) => void
}) {
  const preview = useQuery({
    queryKey: ['campaigns', campaignId, 'preview', campaignContactId],
    queryFn: () =>
      api.get<RecipientPreview>(
        `/campaigns/${campaignId}/contacts/${campaignContactId}/preview`,
      ),
    enabled: campaignContactId !== null,
  })

  const p = preview.data
  const blocked = (p?.preflight.blocking.length ?? 0) > 0 || p?.suppressed.suppressed === true

  return (
    <Drawer
      open={campaignContactId !== null}
      title="Recipient"
      subtitle={p?.contact.email}
      width={680}
      onClose={onClose}
      actions={
        mayReview && p && campaignContactId ? (
          <>
            <Button size="sm" variant="danger" onClick={() => onReject(campaignContactId)}>
              Reject
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={blocked}
              // A control with nowhere to go carries its reason in its accessible name rather
              // than being pointed at nothing.
              aria-label={
                blocked
                  ? 'Approve is unavailable — this message would be refused before it was sent'
                  : 'Approve this recipient'
              }
              onClick={() => onApprove(campaignContactId)}
            >
              Approve
            </Button>
          </>
        ) : undefined
      }
    >
      {preview.isLoading ? (
        <p className="text-faint text-[12.5px]">Loading…</p>
      ) : preview.isError ? (
        <Notice tone="bad">This recipient could not be previewed.</Notice>
      ) : p ? (
        <div className="space-y-4">
          {/* Anything that would stop the send comes first, above the letter. Reading two
              paragraphs of email before discovering it cannot go is the wrong order. */}
          {p.suppressed.suppressed && (
            <Notice tone="bad">
              This address is suppressed{p.suppressed.reason ? ` (${p.suppressed.reason})` : ''}.
              Approving changes nothing — the dispatcher refuses it (§32).
            </Notice>
          )}
          {p.preflight.blocking.map((finding) => (
            <Notice key={finding.code} tone="bad">
              <strong>{finding.code}</strong> — {finding.message}
            </Notice>
          ))}
          {p.preflight.warnings.map((finding) => (
            <Notice key={finding.code} tone="warn">
              <strong>{finding.code}</strong> — {finding.message}
            </Notice>
          ))}

          <section>
            <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">Who this is</h4>
            <dl className="m-0 grid grid-cols-[130px_1fr] gap-x-4 gap-y-1.5 text-[12.5px]">
              <dt className="text-muted">Name</dt>
              <dd className="m-0">
                {p.contact.name ?? <span className="text-faint">unknown</span>}
              </dd>
              <dt className="text-muted">Title</dt>
              <dd className="m-0">{p.contact.title ?? <span className="text-faint">—</span>}</dd>
              <dt className="text-muted">Department</dt>
              <dd className="m-0">
                {p.contact.department ?? <span className="text-faint">—</span>}
              </dd>
              <dt className="text-muted">Institution</dt>
              <dd className="m-0">
                {p.contact.institution ?? <span className="text-faint">—</span>}
              </dd>
              <dt className="text-muted">Verification</dt>
              <dd className="m-0">{p.contact.verification.toLowerCase().replace(/_/g, ' ')}</dd>
              {/* §62. Where the address came from, carried to the moment somebody decides —
                  approving an address whose origin nobody can see is approving on trust. */}
              <dt className="text-muted">Source</dt>
              <dd className="m-0">
                {p.contact.source ? (
                  <>
                    {p.contact.source}
                    {p.contact.capturedAt && (
                      <span className="text-faint">
                        {' '}
                        · captured {new Date(p.contact.capturedAt).toLocaleDateString()}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-faint">not recorded</span>
                )}
              </dd>
              {p.contact.sourceUrl && (
                <>
                  <dt className="text-muted">Source URL</dt>
                  <dd className="text-faint m-0 break-all">{p.contact.sourceUrl}</dd>
                </>
              )}
              <dt className="text-muted">From</dt>
              <dd className="m-0">
                {p.mailbox ? (
                  <>
                    {p.mailbox.displayName} &lt;{p.mailbox.email}&gt;
                    <span className="text-faint"> · {p.mailbox.state.toLowerCase()}</span>
                  </>
                ) : (
                  <span className="text-faint">no mailbox assigned yet</span>
                )}
              </dd>
            </dl>
          </section>

          <section>
            <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">
              What would be sent
              {p.message && (
                <span className="text-faint ml-2 text-[11.5px] font-normal">
                  {p.message.templateName}
                </span>
              )}
            </h4>
            {p.message ? (
              <div className="border-line overflow-hidden rounded-[7px] border">
                <div className="border-line2 border-b px-3 py-2 text-[12.5px]">
                  <span className="text-muted">Subject</span>{' '}
                  <span className="text-text">{p.message.subject}</span>
                </div>
                {/* The text part, as sent. Pre-wrapped rather than rendered as HTML: this is the
                    body of an email inside a review screen, and running someone's markup here
                    would make this the one place in the console that executes content. */}
                <pre className="text-text m-0 max-h-[340px] overflow-auto px-3 py-2.5 font-sans text-[12.5px] whitespace-pre-wrap">
                  {p.message.text}
                </pre>
                {(p.message.unresolved.length > 0 || p.message.empty.length > 0) && (
                  <div className="border-line2 text-faint border-t px-3 py-2 text-[11.5px]">
                    {p.message.unresolved.length > 0 && (
                      <>unresolved: {p.message.unresolved.join(', ')}. </>
                    )}
                    {p.message.empty.length > 0 && <>empty: {p.message.empty.join(', ')}.</>}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted text-[12.5px]">
                Nothing renders yet — this campaign has no approved template version, and §64
                requires the wording to be approved before a recipient can be approved against it.
              </p>
            )}
          </section>

          {p.notes.length > 0 && (
            <section>
              <h4 className="text-text m-0 mb-2 text-[13px] font-semibold">
                What this preview cannot show
              </h4>
              <ul className="text-muted m-0 list-none space-y-1.5 p-0 text-[12px]">
                {p.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      ) : null}
    </Drawer>
  )
}
